import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import FollowButton from "./FollowButton";
import VerificationBadge from "./VerificationBadge";

/**
 * UserCard component for consistent user display across the application
 *
 * Features:
 * - Reusable user display with profile picture and username
 * - Integration with VerificationBadge and FollowButton components
 * - Clickable navigation to user profiles
 * - Compact mode for list displays
 * - Responsive design for different screen sizes
 *
 * Requirements: 9.2, 9.3, 10.1, 10.2, 10.3, 10.4, 10.5
 */
const UserCard = ({
  user,
  showFollowButton = true,
  compact = false,
  onClick,
  className = "",
  followButtonSize = "sm",
  verificationBadgeSize = "medium",
  showProfilePicture = true,
  showUsername = true,
  showVerificationBadge = true,
  ...rest
}) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth0();

  // Don't render if no user data
  if (!user) {
    return null;
  }

  // Handle click navigation to user profile
  const handleCardClick = (e) => {
    // Don't navigate if clicking on interactive elements (but exclude the UserCard itself)
    const clickedButton = e.target.closest("button");
    const clickedLink = e.target.closest("a");
    const clickedRoleButton = e.target.closest("[role='button']");

    // Check if the clicked element is a button or link, but not the UserCard itself
    if (
      (clickedButton && clickedButton !== e.currentTarget) ||
      (clickedLink && clickedLink !== e.currentTarget) ||
      (clickedRoleButton && clickedRoleButton !== e.currentTarget)
    ) {
      return;
    }

    // Always navigate to user profile if user ID exists
    if (user._id || user.id) {
      const userId = user._id || user.id;
      navigate(`/users/${userId}`);

      // Call the onClick callback after navigation (for things like closing modals)
      if (onClick && typeof onClick === "function") {
        onClick(user, e);
      }
    }
  };

  // Determine if this is the current user
  const isCurrentUser =
    currentUser?.sub === user.auth0Id || currentUser?.sub === user._id;

  // Size-specific styles
  const sizeStyles = compact
    ? {
        container: "p-3",
        avatar: "w-10 h-10",
        content: "ml-3",
        username: "text-sm font-medium",
        handle: "text-xs text-medium-grey",
        spacing: "space-y-1",
      }
    : {
        container: "p-4",
        avatar: "w-12 h-12",
        content: "ml-4",
        username: "text-base font-semibold",
        handle: "text-sm text-medium-grey",
        spacing: "space-y-2",
      };

  // Base card classes
  const cardClasses = `
    bg-white rounded-card shadow-card transition-all duration-200
    hover:shadow-lg cursor-pointer
    ${sizeStyles.container}
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  return (
    <div
      className={cardClasses}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick(e);
        }
      }}
      aria-label={`View ${user.username || user.name || "user"}'s profile`}
      {...rest}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center min-w-0 flex-1">
          {/* Profile Picture */}
          {showProfilePicture && (
            <div className="flex-shrink-0">
              <img
                src={user.picture || "/emblem-white.png"}
                alt={`${user.username || user.name || "User"}'s profile`}
                className={`${sizeStyles.avatar} rounded-full object-cover ring-2 ring-light-grey bg-light-grey`}
                onError={(e) => {
                  e.target.src = "/emblem-white.png";
                }}
              />
            </div>
          )}

          {/* User Info */}
          <div className={`${sizeStyles.content} min-w-0 flex-1`}>
            <div className={sizeStyles.spacing}>
              {/* Username and Verification Badge */}
              {showUsername && (
                <div className="flex items-center gap-2 min-w-0">
                  <h3
                    className={`${sizeStyles.username} text-dark-grey truncate`}
                  >
                    {user.username || user.name || "Unknown User"}
                  </h3>
                  {showVerificationBadge && user.verified && (
                    <VerificationBadge
                      verified={user.verified}
                      size={verificationBadgeSize}
                      showTooltip={!compact}
                    />
                  )}
                </div>
              )}

              {/* User Handle/Email (if different from username) */}
              {!compact &&
                user.username &&
                user.name &&
                user.username !== user.name &&
                user.name !== user.email && (
                  <p className={`${sizeStyles.handle} truncate`}>{user.name}</p>
                )}

              {/* Additional user info in non-compact mode */}
              {!compact && (
                <div className="flex items-center gap-4 text-xs text-medium-grey">
                  {user.followerCount !== undefined && (
                    <span>
                      {user.followerCount}{" "}
                      {user.followerCount === 1 ? "follower" : "followers"}
                    </span>
                  )}
                  {user.followingCount !== undefined && (
                    <span>{user.followingCount} following</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Follow Button */}
        {showFollowButton && !isCurrentUser && (user._id || user.id) && (
          <div className="flex-shrink-0 ml-3">
            <FollowButton
              userId={user._id || user.id}
              initialFollowStatus={user.isFollowing}
              size={followButtonSize}
              variant="primary"
            />
          </div>
        )}
      </div>
    </div>
  );
};

UserCard.propTypes = {
  user: PropTypes.shape({
    _id: PropTypes.string,
    id: PropTypes.string,
    auth0Id: PropTypes.string,
    username: PropTypes.string,
    name: PropTypes.string,
    picture: PropTypes.string,
    verified: PropTypes.bool,
    followerCount: PropTypes.number,
    followingCount: PropTypes.number,
    isFollowing: PropTypes.bool,
  }).isRequired,
  showFollowButton: PropTypes.bool,
  compact: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  followButtonSize: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
  verificationBadgeSize: PropTypes.oneOf(["small", "medium", "large"]),
  showProfilePicture: PropTypes.bool,
  showUsername: PropTypes.bool,
  showVerificationBadge: PropTypes.bool,
};

export default UserCard;
